import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { describeError } from "../utils/error_message";
import { useUserDataContext } from "../../user_data_context";
import { normalizeEmail } from "./auth_validation";
import { errorStatus, sendResetCode } from "./auth_api";
import { authErrorKey, validateEmail, type FieldErrors } from "./auth_rules";
import { AuthField, FormError, PRIMARY_BUTTON_CLASS } from "./auth_fields";

interface Props {
	/** Whatever the login form had — the reader rarely needs to type it twice. */
	initialEmail: string;
	onCodeSent: (email: string, token: string) => void;
}

/** Only the address and the button, as the designer asked; "Back" sits above in the modal. */
const RecoveryForm = ({ initialEmail, onCodeSent }: Props) => {
	const { t } = useTranslation();
	const { interfaceLanguage } = useUserDataContext();
	const [email, setEmail] = useState(initialEmail);
	const [errors, setErrors] = useState<FieldErrors>({});
	const [formError, setFormError] = useState("");
	const [loading, setLoading] = useState(false);

	const submit = async (e: FormEvent) => {
		e.preventDefault();
		if (loading) return;
		const found = validateEmail(email);
		setErrors(found);
		setFormError("");
		if (Object.keys(found).length) return;

		setLoading(true);
		try {
			onCodeSent(email, await sendResetCode(email, interfaceLanguage));
		} catch (error) {
			console.error("Landing reset code failed", describeError(error));
			const { field, key } = authErrorKey("send-reset", errorStatus(error));
			if (field === "email") setErrors({ email: key });
			else setFormError(key);
			setLoading(false);
		}
	};

	return (
		<form className="flex flex-col gap-3" onSubmit={submit} noValidate>
			<AuthField
				type="email"
				required
				autoComplete="email"
				placeholder={t("auth-page.inputs.email-placeholder")}
				value={email}
				error={errors.email}
				onChange={e => {
					setEmail(normalizeEmail(e.target.value));
					setErrors({});
					setFormError("");
				}}
			/>
			<FormError error={formError} />
			<button type="submit" className={PRIMARY_BUTTON_CLASS} disabled={loading}>
				{t(loading ? "auth.sending" : "auth.send-code")}
			</button>
		</form>
	);
};

export default RecoveryForm;
